import React, { useEffect, useRef, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useDispatch, useSelector } from "react-redux";
import SalereportTable from "./SalereportTable";
import { getTopSalesItems } from "../ReportSlice";

const SalesReport = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("Week");
  const dropdownRef = useRef(null);

  const dispatch = useDispatch();
  const { loading, topSalesItems, error } = useSelector((state) => state.topSalesItems);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setIsOpen(false);
  };

  useEffect(() => {
    dispatch(getTopSalesItems(selectedOption.toLowerCase()));
  }, [dispatch, selectedOption]);

  // Prepare BarChart data
  const chartData = topSalesItems.map((item) => ({
    name: item.name,
    value: parseFloat(item.total_revenue),
  }));

  // Prepare top and low selling items list
  const sellingItemsData = [...topSalesItems]
    .sort((a, b) => b.total_quantity_sold - a.total_quantity_sold)
    .reduce(
      (acc, item, index, array) => {
        if (index < array.length / 2) acc.top.push(item.name);
        else acc.low.push(item.name);
        return acc;
      },
      { top: [], low: [] }
    );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="p-6 bg-G20 min-h-screen space-y-6">
      <h1 className="text-3xl font-medium">Sale Report</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white shadow rounded-xl p-4 col-span-2">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Overall Sale</h2>
            <div className="relative inline-block" ref={dropdownRef}>
              <button
                onClick={toggleDropdown}
                className="flex justify-center items-center gap-2 text-sm border bg-green50 text-green700 border-G300 rounded-3xl font-semibold px-2 py-1"
              >
                {selectedOption}
                <FaChevronDown size={15} />
              </button>

              {isOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-white border border-gray-300 rounded shadow z-10">
                  <div onClick={() => handleOptionClick("weekly")} className="px-4 py-2 hover:bg-green100 cursor-pointer">Week</div>
                  <div onClick={() => handleOptionClick("monthly")} className="px-4 py-2 hover:bg-green100 cursor-pointer">Month</div>
                  <div onClick={() => handleOptionClick("yearly")} className="px-4 py-2 hover:bg-green100 cursor-pointer">Year</div>
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-10">Loading chart...</div>
          ) : error ? (
            <div className="text-center text-red-500">{error}</div>
          ) : chartData.length === 0 ? (
            <div className="text-center py-10 text-gray-500">No data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} style={{ backgroundColor: "transparent" }}>
                <XAxis dataKey="name" className="text-sm" />
                <YAxis />
                <Tooltip cursor={{ fill: "transparent" }} />
                <Bar dataKey="value" fill="#B6E3C2" radius={[30, 30, 0, 0]} barSize={25} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white shadow rounded-xl overflow-hidden">
          <div className="grid grid-cols-2 text-sm font-semibold text-white bg-green">
            <div className="p-5 text-center">Top selling items</div>
            <div className="p-5 text-center">Low selling items</div>
          </div>

          {loading ? (
            <div className="text-center py-10">Loading items...</div>
          ) : error ? (
            <div className="text-center text-red-500">{error}</div>
          ) : sellingItemsData.top.length === 0 && sellingItemsData.low.length === 0 ? (
            <div className="text-center py-10 text-gray-500">No data available</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {sellingItemsData.top.map((topItem, index) => (
                <div key={index} className="grid grid-cols-2 text-center text-sm">
                  <div className="py-8 border-r border-G30">{topItem}</div>
                  <div className="py-8">{sellingItemsData.low[index] || "-"}</div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>

      <div className="text-green">
        <SalereportTable />
      </div>
    </div>
  );
};

export default SalesReport;
